import { useEffect, useState } from 'react';
import { fetchMe, type Me } from './api/client.js';
import { inZoomClient } from './adapters/index.js';
import { AdminApp } from './AdminApp.js';
import { OperatorApp } from './OperatorApp.js';
import { SimApp } from './SimApp.js';
import { RegisterPage } from './RegisterPage.js';
import { ZoomSdkAdapter } from './adapters/zoomSdkAdapter.js';

/**
 * One shell, three audiences, decided by who the server says is calling:
 *  - a CrtQ admin (signed in at crtq.ai) gets the admin console
 *  - a Zoom operator inside the client gets the operator console
 *  - the development identity (DEV_FAKE_CONTEXT=1 only) gets the original all-in-one app
 * Anyone else sees only a sign-in prompt. The shell itself carries no data.
 */
export function App() {
  // The two short sign-up links are public pages and never touch sign-in.
  const link = location.pathname.match(/^\/[js]\/([A-Za-z0-9]{6,12})\/?$/);
  if (link) return <RegisterPage code={link[1]!} />;
  return <Authenticated />;
}

function Authenticated() {
  const [me, setMe] = useState<Me | null>(null);
  const [zoomStep, setZoomStep] = useState<string>('ok');
  const [, bump] = useState(0);
  useEffect(() => {
    ZoomSdkAdapter.onLog = () => bump((n) => n + 1);
    return () => {
      ZoomSdkAdapter.onLog = null;
    };
  }, []);
  useEffect(() => {
    void (async () => {
      // Inside Zoom, get the identity from the SDK first so every call can carry it.
      if (inZoomClient() && !new URLSearchParams(location.search).has('sim')) setZoomStep(await ZoomSdkAdapter.primeIdentity());
      setMe(await fetchMe());
    })();
  }, []);

  if (!me) {
    return (
      <div>
        <p className="muted">Starting…</p>
        {ZoomSdkAdapter.log.length > 0 && <pre className="muted" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{ZoomSdkAdapter.log.join('\n')}</pre>}
      </div>
    );
  }
  if (me.kind === 'dev') return <SimApp />;
  if (me.kind === 'admin') return <AdminApp me={me} />;
  if (me.kind === 'operator' && inZoomClient()) return <OperatorApp />;

  return (
    <div>
      <h1>Rotation Controller</h1>
      {inZoomClient() ? (
        <div className="banner banner-error" role="alert">
          <p>Zoom did not identify you to this app. Close and reopen the app from the Apps panel. If it keeps happening, tell the organiser.</p>
          <p className="muted">
            What happened: {zoomStep !== 'ok' ? zoomStep : me.detail ?? 'no further detail'}
            {zoomStep !== 'ok' && me.detail ? ` (server: ${me.detail})` : ''}
          </p>
          {ZoomSdkAdapter.log.length > 0 && <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{ZoomSdkAdapter.log.join('\n')}</pre>}
        </div>
      ) : (
        <>
          <p>Event administration requires a CrtQ sign-in.</p>
          <p>
            <a className="primary" href="/auth/login" style={{ display: 'inline-block', padding: '6px 12px', textDecoration: 'none', color: '#fff', background: '#0b5fff', borderRadius: 4 }}>
              Sign in with CrtQ
            </a>
          </p>
          <p className="muted">Running an evening in Zoom? Open this app from the Zoom client instead; you will be asked for a join code from your organiser.</p>
        </>
      )}
    </div>
  );
}
